import { useCallback, useEffect, useRef, useState } from 'react';

import type { ScrollState } from './useScrollObserver';

const INITIAL_STATE: ScrollState = { canScrollUp: false, canScrollDown: false, scrollDirection: null };

/**
 * Tracks scroll state of a container: can scroll up/down and scroll direction.
 * Only triggers re-renders when boolean values or direction actually change.
 *
 * Returns [state, callbackRef] — pass the callbackRef to the scroll container's ref prop.
 * Setup and teardown happen directly in the ref callback, no useLayoutEffect needed.
 */
export const useScrollIndicator = (): [ScrollState, (node: HTMLDivElement | null) => void] => {
  const [state, setState] = useState<ScrollState>(INITIAL_STATE);
  const prevState = useRef(INITIAL_STATE);
  const lastScrollTop = useRef(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  const callbackRef = useCallback((element: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!element) {
      prevState.current = INITIAL_STATE;
      setState(INITIAL_STATE);

      return;
    }

    lastScrollTop.current = element.scrollTop;

    const update = () => {
      const canScrollUp = element.scrollTop > 0;
      const canScrollDown = Math.ceil(element.scrollTop + element.clientHeight) < element.scrollHeight;
      const scrollDirection =
        element.scrollTop === lastScrollTop.current
          ? prevState.current.scrollDirection
          : element.scrollTop > lastScrollTop.current
            ? 'down'
            : 'up';

      lastScrollTop.current = element.scrollTop;

      if (
        canScrollUp !== prevState.current.canScrollUp ||
        canScrollDown !== prevState.current.canScrollDown ||
        scrollDirection !== prevState.current.scrollDirection
      ) {
        const next = { canScrollUp, canScrollDown, scrollDirection };
        prevState.current = next;
        setState(next);
      }
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    element.addEventListener('scroll', update, { passive: true });

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true });

    cleanupRef.current = () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      element.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return [state, callbackRef];
};
